/**
 * @fileoverview ApprovalScopeNote — tells an approver whose time their queue
 * holds, when it is not simply everyone's (MOR-1017).
 *
 * A firm-wide approver sees nothing: their queue is the firm, as it always
 * was. A manager limited to teams is told which teams, and an admin stepping
 * in for people no approver covers is told that — otherwise an empty or
 * short queue looks like a bug rather than a setting.
 */

import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { Users } from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";

function describeScope(
  kind: "firm" | "teams" | "fallback" | "none",
  teamNames: string[],
): string | null {
  if (kind === "teams") {
    return teamNames.length > 0
      ? `You approve time for ${teamNames.length === 1 ? "the team" : "the teams"} ${teamNames.join(", ")}. Other people's time goes to their own approver.`
      : "You are set to approve selected teams, but no team has been chosen yet, so nothing will reach you. An admin can fix this in Timesheet Settings.";
  }
  if (kind === "fallback") {
    return "You are seeing time from people no approver covers (on no team, or on a team without an approver). Everyone else's time goes to their team's approver.";
  }
  return null;
}

export function ApprovalScopeNote({ orgId }: { orgId: Id<"organizations"> }) {
  const { data: scope } = useQuery(
    convexQuery(api.timesheets.entries.approvalScopeSummary, { orgId }),
  );
  const message = scope ? describeScope(scope.kind, scope.teamNames) : null;
  if (!message) return null;
  return (
    <p
      role="note"
      className="flex items-start gap-2 rounded-lg border border-border/60 bg-background px-3 py-2 text-sm text-muted-foreground"
    >
      <Users className="mt-0.5 size-4 shrink-0" />
      {message}
    </p>
  );
}
